import { useLocation } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { countPendingDocuments } from '@/db/services/document_service';
import { countPendingCaseNotes } from '@/db/services/case_note_service';
import { AuthExpiredError } from '@/lib/fetch-utils';
import { useBanner } from '@/hooks/useBanner';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { useServerOnline } from '@/hooks/useServerOnline';
import { useSyncActivity } from '@/hooks/useSyncActivity';
import { isAuthRoute, unsyncedWorkMessage } from './messages';

/**
 * Posts the "unsynced-work" message: a failed sync on any page, and documents
 * and notes waiting to upload on the dashboard (see unsyncedWorkMessage). They
 * are counted on the dashboard only, so editor autosaves never flash the banner.
 */
export function useUnsyncedWorkBanner(): void {
  const { organizationId } = useCurrentPerson();
  const online = useServerOnline();
  const activity = useSyncActivity();
  const { pathname } = useLocation();
  const onDashboard = pathname === '/';
  const signedIn = organizationId !== null && !isAuthRoute(pathname);

  const countPending = signedIn && onDashboard;
  const pendingCount = useLiveQuery(
    async () => {
      if (!countPending) return 0;
      const [documents, notes] = await Promise.all([countPendingDocuments(organizationId!), countPendingCaseNotes(organizationId!)]);
      return documents + notes;
    },
    [countPending, organizationId],
  ) ?? 0;

  useBanner(
    signedIn
      ? unsyncedWorkMessage({
          pendingCount,
          online,
          onDashboard,
          syncFailed: activity.status === 'error' && !(activity.lastError instanceof AuthExpiredError),
          failedAt: activity.finishedAt,
          syncingVisibly: activity.status === 'syncing' && activity.visible,
        })
      : null,
  );
}
